// src/components/AlertDialog.js

import React, { useContext, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { LanguageContext } from '../contexts/LanguageContext';
import './AlertDialog.css';

const AlertDialog = ({
  show,
  onHide,
  title,
  message,
  variant = 'info', // success, danger, warning, info
  icon = null,
  autoClose = false,
  autoCloseDelay = 3000,
  showCloseButton = true,
}) => {
  const { translations } = useContext(LanguageContext);

  useEffect(() => {
    if (show && autoClose) {
      const timer = setTimeout(() => {
        onHide();
      }, autoCloseDelay);

      return () => clearTimeout(timer);
    }
  }, [show, autoClose, autoCloseDelay, onHide]);

  const getIcon = () => {
    if (icon) return icon;
    
    switch (variant) {
      case 'success':
        return <i className="bi bi-check-circle-fill text-success"></i>;
      case 'danger':
        return <i className="bi bi-exclamation-triangle-fill text-danger"></i>;
      case 'warning':
        return <i className="bi bi-exclamation-triangle-fill text-warning"></i>;
      case 'info':
      default:
        return <i className="bi bi-info-circle-fill text-info"></i>;
    }
  };

  const getDefaultTitle = () => {
    switch (variant) {
      case 'success':
        return translations.success || 'Success';
      case 'danger':
        return translations.error || 'Error';
      case 'warning':
        return translations.warning || 'Warning';
      case 'info':
      default:
        return translations.information || 'Information';
    }
  };

  const getDefaultMessage = () => {
    switch (variant) {
      case 'success':
        return translations.operationSuccessful || 'Operation completed successfully.';
      case 'danger':
        return translations.errorOccurred || 'An error occurred.';
      case 'warning':
        return translations.warningMessage || 'Please review the information.';
      case 'info':
        return translations.informationMessage || 'Information available.';
      default:
        return translations.informationMessage || 'Information available.';
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      className={`alert-dialog variant-${variant}`}
      backdrop={autoClose ? true : "static"}
      keyboard={true}
    >
      {showCloseButton && (
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <span className="me-2">{getIcon()}</span>
            {title || getDefaultTitle()}
          </Modal.Title>
        </Modal.Header>
      )}
      <Modal.Body className={!showCloseButton ? 'pt-4' : ''}>
        {!showCloseButton && (
          <div className="text-center mb-3">
            <div className="alert-icon-large">
              {getIcon()}
            </div>
            <h5 className="mt-2 mb-3">{title || getDefaultTitle()}</h5>
          </div>
        )}
        <Alert variant={variant} className="border-0 bg-transparent p-0 m-0">
          <p className="mb-0 text-center">
            {message || getDefaultMessage()}
          </p>
        </Alert>
        {autoClose && (
          <div className="text-center mt-3">
            <small className="text-muted">
              {translations.dialogWillClose || 'Dialog will close automatically'}
            </small>
          </div>
        )}
      </Modal.Body>
      {!autoClose && (
        <Modal.Footer className="justify-content-center">
          <Button variant={variant} onClick={onHide}>
            {translations.ok || 'OK'}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default AlertDialog;
